import {
  baseApi,
  ERROR_CODES as API_ERROR_CODES,
  getErrorMessage,
} from '@/shared/api';
import type {
  DimensionColumnDataType,
  DimensionDataFormatType,
} from '@/shared/types';
import { mapUser } from '../libs/mapUser';
import { ERROR_CODES as UI_ERROR_CODES } from '../model/constants';
import type { User, UserSettingsMeta } from '../model/types';
import type { UserDTO } from './types';

export const getOrganizationUsers = async (): Promise<User[]> => {
  const { data, error } = await baseApi.functions.invoke<{
    users: UserDTO[];
  }>('organization-users', {
    method: 'GET',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data.users.map(mapUser);
};

export const inviteUserToOrganization = async (
  email: string,
  roleId: string,
): Promise<User> => {
  const { data, error } = await baseApi.functions.invoke<UserDTO>(
    'organization-users',
    {
      body: { user: { email, roleId } },
      method: 'POST',
    },
  );

  if (!data || error) {
    let errorMessage: string = '';

    if (error.context.status === 422) {
      const errorContext = await error.context.json();

      if (errorContext.errorCode === API_ERROR_CODES.EMAIL_EXISTS) {
        // TODO: create custom error class
        // eslint-disable-next-line no-throw-literal
        throw {
          message: 'Email already registered.',
          code: UI_ERROR_CODES.EMAIL_EXISTS,
        };
      } else {
        errorMessage = errorContext.error;
      }
    }

    throw new Error(errorMessage || getErrorMessage(error));
  }

  return mapUser(data);
};

export const updateUser = async (updatedUser: {
  id: string;
  roleId?: string;
  isActive?: boolean;
}): Promise<User> => {
  const { id, ...updatedUserData } = updatedUser;

  const { data, error } = await baseApi.functions.invoke<UserDTO>(
    `organization-users/${id}`,
    {
      body: {
        user: updatedUserData,
      },
      method: 'PUT',
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return mapUser(data);
};

export const updateUsersBatch = async (
  users: Array<{ userId: string; roleId?: string; isActive?: boolean }>,
): Promise<User[]> => {
  const { data, error } = await baseApi.functions.invoke<{ users: UserDTO[] }>(
    'organization-users/batch',
    { body: { users }, method: 'PATCH' },
  );

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data.users.map(mapUser);
};

export const getUserSettingsMeta = async (): Promise<UserSettingsMeta> => {
  const { data, error } = await baseApi.functions.invoke<{
    rlsColumns: string[];
    clsColumns: string[];
    config: Record<
      string,
      {
        name: string;
        description: string;
        group: string;
        dataType: DimensionDataFormatType;
        type: DimensionColumnDataType;
        order: number;
      }
    >;
  }>('organization-users/user-settings-meta', {
    method: 'GET',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

export const resendInvite = async (email: string): Promise<void> => {
  const { error } = await baseApi.functions.invoke<void>(
    'organization-users/resend-invite',
    {
      method: 'POST',
      body: { email },
    },
  );

  if (error) {
    throw new Error(getErrorMessage(error));
  }
};
